import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { adminAPI } from '../../services/api';
import './AppSettings.css';

const AppSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('general');
  const [hasChanges, setHasChanges] = useState(false);
  
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  // Default settings structure
  const defaultSettings = {
    // General
    allowRegistration: true,
    requireEmailVerification: false,
    defaultUserRole: 'user',
    
    // Meeting
    maxParticipantsPerRoom: 10,
    defaultRoomDuration: 60,
    allowRecording: true,
    maxRecordingDuration: 120,
    
    // Features
    enableChat: true,
    enableScreenShare: true,
    enableFileSharing: true,
    enableVirtualBackground: true,
    
    // Security
    maintenanceMode: false,
    require2FA: false,
    sessionTimeout: 60,
    maxLoginAttempts: 5,
    
    // Notifications
    emailNotifications: true,
    pushNotifications: true,
    meetingReminders: true,
    reminderTime: 15,
    
    // Appearance
    appName: 'VideoConf',
    primaryColor: '#667eea',
    allowThemeSwitch: true,
    
    // Limits
    maxMeetingsPerDay: 10,
    maxInviteesPerMeeting: 50,
    maxScheduledDaysAhead: 30
  };

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/admin');
      return;
    }
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getSettings();
      setSettings({ ...defaultSettings, ...response.data });
    } catch (err) {
      setSettings(defaultSettings);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
    setSuccess('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const updates = {
        allowRegistration: settings.allowRegistration,
        requireEmailVerification: settings.requireEmailVerification,
        maxParticipantsPerRoom: parseInt(settings.maxParticipantsPerRoom),
        defaultRoomDuration: parseInt(settings.defaultRoomDuration),
        enableChat: settings.enableChat,
        enableScreenShare: settings.enableScreenShare,
        enableRecording: settings.enableRecording,
        maintenanceMode: settings.maintenanceMode,
        appName: settings.appName,
        primaryColor: settings.primaryColor,
        allowThemeSwitch: settings.allowThemeSwitch,
        emailNotifications: settings.emailNotifications,
        pushNotifications: settings.pushNotifications,
        meetingReminders: settings.meetingReminders,
        reminderTime: parseInt(settings.reminderTime),
        require2FA: settings.require2FA,
        sessionTimeout: parseInt(settings.sessionTimeout),
        maxLoginAttempts: parseInt(settings.maxLoginAttempts),
        allowFileSharing: settings.allowFileSharing,
        allowVirtualBackground: settings.allowVirtualBackground,
        maxMeetingsPerDay: parseInt(settings.maxMeetingsPerDay),
        maxInviteesPerMeeting: parseInt(settings.maxInviteesPerMeeting),
        maxScheduledDaysAhead: parseInt(settings.maxScheduledDaysAhead),
        maxRecordingDuration: parseInt(settings.maxRecordingDuration),
        defaultUserRole: settings.defaultUserRole
      };
      
      await adminAPI.updateSettings(updates);
      setSuccess('Paramètres enregistrés avec succès!');
      setHasChanges(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir réinitialiser tous les paramètres par défaut ?')) {
      return;
    }
    
    try {
      await adminAPI.resetSettings();
      await loadSettings();
      setSuccess('Paramètres réinitialisés avec succès!');
      setHasChanges(false);
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return <div className="admin-loading">Chargement...</div>;
  }

  return (
    <div className="settings-page">
      <div className="settings-header">
        <div>
          <h1>Paramètres de l'Application</h1>
          <p>Configurez les options globales de la plateforme</p>
        </div>
        {hasChanges && (
          <div className="unsaved-badge">
            Modifications non enregistrées
          </div>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Settings Tabs */}
      <div className="settings-tabs">
        <button 
          className={`tab ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          Général
        </button>
        <button 
          className={`tab ${activeTab === 'meeting' ? 'active' : ''}`}
          onClick={() => setActiveTab('meeting')}
        >
          Réunions
        </button>
        <button 
          className={`tab ${activeTab === 'features' ? 'active' : ''}`}
          onClick={() => setActiveTab('features')}
        >
          Fonctionnalités
        </button>
        <button 
          className={`tab ${activeTab === 'security' ? 'active' : ''}`}
          onClick={() => setActiveTab('security')}
        >
          Sécurité
        </button>
        <button 
          className={`tab ${activeTab === 'notifications' ? 'active' : ''}`}
          onClick={() => setActiveTab('notifications')}
        >
          Notifications
        </button>
      </div>

      {/* Settings Content */}
      <div className="settings-content">
        {activeTab === 'general' && (
          <div className="settings-section">
            <h2>Paramètres Généraux</h2>
            
            <div className="setting-group">
              <h3>Inscription</h3>
              <div className="setting-item">
                <div className="setting-info">
                  <label>Autoriser l'inscription</label>
                  <p>Permettre aux nouveaux utilisateurs de créer un compte</p>
                </div>
                <label className="toggle">
                  <input 
                    type="checkbox"
                    checked={settings.allowRegistration}
                    onChange={(e) => handleChange('allowRegistration', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              
              <div className="setting-item">
                <div className="setting-info">
                  <label>Vérification d'email requise</label>
                  <p>Les utilisateurs doivent vérifier leur adresse email</p>
                </div>
                <label className="toggle">
                  <input 
                    type="checkbox"
                    checked={settings.requireEmailVerification}
                    onChange={(e) => handleChange('requireEmailVerification', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              
              <div className="setting-item">
                <div className="setting-info">
                  <label>Rôle par défaut</label>
                  <p>Rôle attribué aux nouveaux utilisateurs</p>
                </div>
                <select 
                  value={settings.defaultUserRole}
                  onChange={(e) => handleChange('defaultUserRole', e.target.value)}
                >
                  <option value="user">Utilisateur</option>
                  <option value="Presentateur">Présentateur</option>
                </select>
              </div>
            </div>

            <div className="setting-group">
              <h3>Apparence</h3>
              <div className="setting-item">
                <div className="setting-info">
                  <label>Nom de l'application</label>
                  <p>Nom affiché dans l'en-tête</p>
                </div>
                <input 
                  type="text"
                  value={settings.appName}
                  onChange={(e) => handleChange('appName', e.target.value)}
                />
              </div>
              
              <div className="setting-item">
                <div className="setting-info">
                  <label>Couleur primaire</label>
                  <p>Couleur principale de l'interface</p>
                </div>
                <div className="color-input">
                  <input 
                    type="color"
                    value={settings.primaryColor}
                    onChange={(e) => handleChange('primaryColor', e.target.value)}
                  />
                  <span>{settings.primaryColor}</span>
                </div>
              </div>
              
              <div className="setting-item">
                <div className="setting-info">
                  <label>Autoriser le changement de thème</label>
                  <p>Permettre aux utilisateurs de changer le thème</p>
                </div>
                <label className="toggle">
                  <input 
                    type="checkbox"
                    checked={settings.allowThemeSwitch}
                    onChange={(e) => handleChange('allowThemeSwitch', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            <div className="setting-group">
              <h3>Mode Maintenance</h3>
              <div className="setting-item">
                <div className="setting-info">
                  <label>Mode maintenance</label>
                  <p>Empêche l'accès à l'application (sauf admins)</p>
                </div>
                <label className="toggle maintenance-toggle">
                  <input 
                    type="checkbox"
                    checked={settings.maintenanceMode}
                    onChange={(e) => handleChange('maintenanceMode', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'meeting' && (
          <div className="settings-section">
            <h2>Paramètres des Réunions</h2>
            
            <div className="setting-group">
              <h3>Capacité</h3>
              <div className="setting-item">
                <div className="setting-info">
                  <label>Participants max par salle</label>
                  <p>Nombre maximum de participants dans une réunion</p>
                </div>
                <input 
                  type="number"
                  value={settings.maxParticipantsPerRoom}
                  onChange={(e) => handleChange('maxParticipantsPerRoom', e.target.value)}
                  min="2"
                  max="100"
                />
              </div>
              
              <div className="setting-item">
                <div className="setting-info">
                  <label>Durée par défaut (minutes)</label>
                  <p>Durée par défaut d'une nouvelle réunion</p>
                </div>
                <input 
                  type="number"
                  value={settings.defaultRoomDuration}
                  onChange={(e) => handleChange('defaultRoomDuration', e.target.value)}
                  min="15"
                  max="480"
                />
              </div>
            </div>

            <div className="setting-group">
              <h3>Planification</h3>
              <div className="setting-item">
                <div className="setting-info">
                  <label>Réunions max par jour</label>
                  <p>Nombre maximum de réunions qu'un utilisateur peut planifier</p>
                </div>
                <input 
                  type="number"
                  value={settings.maxMeetingsPerDay}
                  onChange={(e) => handleChange('maxMeetingsPerDay', e.target.value)}
                  min="1"
                  max="100"
                />
              </div>
              
              <div className="setting-item">
                <div className="setting-info">
                  <label>Invités max par réunion</label>
                  <p>Nombre maximum d'invités par réunion</p>
                </div>
                <input 
                  type="number"
                  value={settings.maxInviteesPerMeeting}
                  onChange={(e) => handleChange('maxInviteesPerMeeting', e.target.value)}
                  min="1"
                  max="500"
                />
              </div>
              
              <div className="setting-item">
                <div className="setting-info">
                  <label>Jours max à l'avance</label>
                  <p>Combien de jours à l'avance on peut planifier</p>
                </div>
                <input 
                  type="number"
                  value={settings.maxScheduledDaysAhead}
                  onChange={(e) => handleChange('maxScheduledDaysAhead', e.target.value)}
                  min="1"
                  max="365"
                />
              </div>
            </div>

            <div className="setting-group">
              <h3>Enregistrement</h3>
              <div className="setting-item">
                <div className="setting-info">
                  <label>Autoriser l'enregistrement</label>
                  <p>Permettre l'enregistrement des réunions</p>
                </div>
                <label className="toggle">
                  <input 
                    type="checkbox"
                    checked={settings.enableRecording}
                    onChange={(e) => handleChange('enableRecording', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              
              <div className="setting-item">
                <div className="setting-info">
                  <label>Durée max d'enregistrement (min)</label>
                  <p>Durée maximale d'un enregistrement</p>
                </div>
                <input 
                  type="number"
                  value={settings.maxRecordingDuration}
                  onChange={(e) => handleChange('maxRecordingDuration', e.target.value)}
                  min="30"
                  max="480"
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'features' && (
          <div className="settings-section">
            <h2>Fonctionnalités</h2>
            
            <div className="setting-group">
              <h3>Communication</h3>
              <div className="setting-item">
                <div className="setting-info">
                  <label>Activer le chat</label>
                  <p>Permettre le chat pendant les réunions</p>
                </div>
                <label className="toggle">
                  <input 
                    type="checkbox"
                    checked={settings.enableChat}
                    onChange={(e) => handleChange('enableChat', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              
              <div className="setting-item">
                <div className="setting-info">
                  <label>Partage d'écran</label>
                  <p>Permettre le partage d'écran</p>
                </div>
                <label className="toggle">
                  <input 
                    type="checkbox"
                    checked={settings.enableScreenShare}
                    onChange={(e) => handleChange('enableScreenShare', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              
              <div className="setting-item">
                <div className="setting-info">
                  <label>Partage de fichiers</label>
                  <p>Permettre le partage de fichiers</p>
                </div>
                <label className="toggle">
                  <input 
                    type="checkbox"
                    checked={settings.allowFileSharing}
                    onChange={(e) => handleChange('allowFileSharing', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              
              <div className="setting-item">
                <div className="setting-info">
                  <label>Arrière-plan virtuel</label>
                  <p>Permettre les arrière-plans virtuels</p>
                </div>
                <label className="toggle">
                  <input 
                    type="checkbox"
                    checked={settings.allowVirtualBackground}
                    onChange={(e) => handleChange('allowVirtualBackground', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'security' && (
          <div className="settings-section">
            <h2>Sécurité</h2>
            
            <div className="setting-group">
              <h3>Authentification</h3>
              <div className="setting-item">
                <div className="setting-info">
                  <label>Exiger 2FA</label>
                  <p>Exiger l'authentification à deux facteurs</p>
                </div>
                <label className="toggle">
                  <input 
                    type="checkbox"
                    checked={settings.require2FA}
                    onChange={(e) => handleChange('require2FA', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              
              <div className="setting-item">
                <div className="setting-info">
                  <label>Timeout de session (minutes)</label>
                  <p>Durée d'inactivité avant déconnexion</p>
                </div>
                <input 
                  type="number"
                  value={settings.sessionTimeout}
                  onChange={(e) => handleChange('sessionTimeout', e.target.value)}
                  min="5"
                  max="480"
                />
              </div>
              
              <div className="setting-item">
                <div className="setting-info">
                  <label>Essais de connexion max</label>
                  <p>Nombre max de tentatives avant verrouillage</p>
                </div>
                <input 
                  type="number"
                  value={settings.maxLoginAttempts}
                  onChange={(e) => handleChange('maxLoginAttempts', e.target.value)}
                  min="3"
                  max="10"
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="settings-section">
            <h2>Notifications</h2>
            
            <div className="setting-group">
              <h3>Types de notifications</h3>
              <div className="setting-item">
                <div className="setting-info">
                  <label>Notifications par email</label>
                  <p>Envoyer des notifications par email</p>
                </div>
                <label className="toggle">
                  <input 
                    type="checkbox"
                    checked={settings.emailNotifications}
                    onChange={(e) => handleChange('emailNotifications', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              
              <div className="setting-item">
                <div className="setting-info">
                  <label>Notifications push</label>
                  <p>Envoyer des notifications push</p>
                </div>
                <label className="toggle">
                  <input 
                    type="checkbox"
                    checked={settings.pushNotifications}
                    onChange={(e) => handleChange('pushNotifications', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              
              <div className="setting-item">
                <div className="setting-info">
                  <label>Rappels de réunion</label>
                  <p>Envoyer des rappels avant les réunions</p>
                </div>
                <label className="toggle">
                  <input 
                    type="checkbox"
                    checked={settings.meetingReminders}
                    onChange={(e) => handleChange('meetingReminders', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              
              {settings.meetingReminders && (
                <div className="setting-item">
                  <div className="setting-info">
                    <label>Temps de rappel (minutes)</label>
                    <p>Minutes avant la réunion pour le rappel</p>
                  </div>
                  <input 
                    type="number"
                    value={settings.reminderTime}
                    onChange={(e) => handleChange('reminderTime', e.target.value)}
                    min="5"
                    max="60"
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="settings-actions">
        <button 
          className="btn-secondary"
          onClick={handleReset}
        >
          Réinitialiser
        </button>
        <button 
          className="btn-primary"
          onClick={handleSave}
          disabled={!hasChanges || saving}
        >
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      </div>
    </div>
  );
};

export default AppSettings;
